<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import Banner from '@/components/ComplexBanner.vue'
import { integrationsModule } from '@/store/integrations/module'
import { toastModule } from '@/store/toast/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'

type SelectEntry = {
    value: string
    label: string
}

const {
    ModalContainer,
    SidePanel,
    Button: PocketButton,
    Select: PocketSelect,
    Input: PocketInput,
    Errors,
    Icon,
    Tooltip,
} = UIKit

const props = withDefaults(
    defineProps<{
        clientId?: string
        lmsId?: string
        courseIntegrationId?: string
        orgExamId?: string
        instructors?: string[]
        existingGroup?: SelectEntry
    }>(),
    {
        clientId: '',
        lmsId: '',
        courseIntegrationId: '',
        orgExamId: '',
        instructors: () => [],
        existingGroup: () => ({} as SelectEntry),
    }
)

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const deploymentId = ref('')
const courseName = ref('')
const isLoading = ref(false)
const errors = ref<string[]>([])

const showRemoveInstructorTooltip = ref<null | string>(null)
const showAddInstructorTooltip = ref<null | string>(null)
const showArchiveCourseIntegrationModal = ref(false)
const instructorOptions = ref<{ value: string; label: string }[]>([])

const exam = ref<null | SelectEntry>(null)
const group = ref<null | SelectEntry>(null)

const instructorSelectedIds = ref<string[]>([])

const closeArchiveModal = () => {
    showArchiveCourseIntegrationModal.value = false
}

const openArchiveModal = () => {
    showArchiveCourseIntegrationModal.value = true
}

const setRemoveInstructorTooltip = (id: string | null) => {
    showRemoveInstructorTooltip.value = id
}

const clearRemoveInstructorTooltip = () => {
    showRemoveInstructorTooltip.value = null
}

const setAddInstructorTooltip = (id: string | null) => {
    showAddInstructorTooltip.value = id
}

const clearAddInstructorTooltip = () => {
    showAddInstructorTooltip.value = null
}

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const mappedExams = computed(() => {
    return Object.values(currentOrgExams.value).map(coe => ({ label: coe.displayName, value: coe.objectId }))
})

const currentCourseIntegrations = computed(() => {
    return integrationsModule.getters.getCurrentCourseIntegrations()
})

const currentCourseIntegration = computed(() => {
    return currentCourseIntegrations.value?.find(cci => cci.objectId === props.courseIntegrationId)
})

const mappedGroups = computed(() => {
    return Object.values(currentOrgGroups.value).reduce((acc, cog) => {
        if (cog.orgExam.objectId === exam.value?.value && cog.type !== 'system') {
            acc.push({ label: cog.name, value: cog.objectId })
        }
        return acc
    }, [] as SelectEntry[])
})

const getDisplayNameForInstructor = (instructorId: string) => {
    const inst = currentOrgInstructors.value?.[instructorId]
    if (!inst) {
        return
    }
    const instructorUser = currentOrgInstructorUsers.value?.[inst.user.objectId]
    if (!instructorUser) {
        return
    }
    const firstString = instructorUser?.firstName || instructorUser?.email
    const lastName = instructorUser?.lastName
    return lastName ? `${firstString} ${lastName}` : firstString
}

const mappedInstructors = computed(() => {
    return Object.values(currentOrgInstructors.value || []).map(coi => {
        const instructorString = getDisplayNameForInstructor(coi.objectId)
        return { label: instructorString || '', value: coi.objectId }
    })
})

const getExistingInstructors = (): SelectEntry[] => {
    return props.instructors.reduce((acc, i) => {
        const instructorString = getDisplayNameForInstructor(i)
        if (instructorString) {
            acc.push({ label: instructorString, value: i })
        }
        return acc
    }, [] as SelectEntry[])
}

const getOrgGroupsForInstructorAndExam = (instructorId: string, orgExamId: string) => {
    return Object.values(currentOrgGroups.value).filter(g => {
        return g.type === 'system'
            && g.owner
            && g.owner.objectId === instructorId
            && g.orgExam.objectId === orgExamId
    })
}

const emitClose = () => {
    emit('close', true)
}

const editIntegration = async () => {
    isLoading.value = true
    const finalInstructorIds = instructorOptions.value.map(io => io.value)
    const orgExamId = exam.value?.value
    if (orgExamId) {
        const instructorOrgGroupIds = finalInstructorIds
            .flatMap(isi => getOrgGroupsForInstructorAndExam(isi, orgExamId))
            .map(g => g.objectId)

        const examOrgGroupId = (groupModule.getters.searchCurrentOrgGroups(
            { examId: orgExamId, type: 'system' }
        ).find(eog => !eog.owner))?.objectId

        const selectedOrgGroupId = group.value?.value

        const orgGroupIds = [ ...instructorOrgGroupIds, examOrgGroupId, selectedOrgGroupId ].filter(id =>
            id !== undefined
        )

        await integrationsModule.actions.upsertLMSCourseIntegration({
            deploymentId: deploymentId.value,
            lmsId: props.lmsId,
            orgGroupIds,
            courseName: courseName.value,
            lmsCourseIntegrationId: currentCourseIntegration.value?.objectId,
        })

        toastModule.actions.displayToast({
            title: 'Canvas course edited.',
        })
        emitClose()

        await integrationsModule.actions.fetchLMSCourseIntegrations(true)
    }
    isLoading.value = false
}

const archiveIntegration = async () => {
    const current = currentCourseIntegration.value
    if (current) {
        await integrationsModule.actions.toggleLMSCourseIntegration({
            lmsCourseIntegrationId: current.objectId,
        })

        toastModule.actions.displayToast({
            title: 'Canvas course archived.',
        })

        await integrationsModule.actions.fetchLMSCourseIntegrations(true)
        emitClose()
    }
}

const addInstructor = (instructorId?: string) => {
    if (!instructorId) {
        return
    }
    showAddInstructorTooltip.value = null
    instructorSelectedIds.value.push(instructorId)
    instructorOptions.value.push({ value: '', label: '' })
}

const removeInstructor = (instructorId: string) => {
    showRemoveInstructorTooltip.value = null
    const instructorIdIndex = instructorSelectedIds.value.indexOf(instructorId)
    instructorSelectedIds.value.splice(instructorIdIndex, 1)

    const instructorOptionIndex = instructorOptions.value.findIndex(io => io.value === instructorId)
    instructorOptions.value.splice(instructorOptionIndex, 1)

    if (!instructorOptions.value.length) {
        instructorOptions.value.push({ value: '', label: '' })
    }
}

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        integrationsModule.actions.fetchLMSIntegrations(),
        groupModule.actions.fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
    ])

    await integrationsModule.actions.fetchLMSCourseIntegrations(true)

    deploymentId.value = currentCourseIntegration.value?.deploymentId || ''
    courseName.value = currentCourseIntegration.value?.name || ''
    exam.value = {
        label: currentOrgExams.value[props.orgExamId]?.displayName || '',
        value: props.orgExamId,
    }

    group.value = props.existingGroup && props.existingGroup.label !== '--' ? props.existingGroup : null

    if (props.instructors.length) {
        instructorOptions.value = [ ...instructorOptions.value, ...getExistingInstructors() ]
    } else {
        instructorOptions.value.push({ value: '', label: '' })
    }
})
</script>

<template>
    <Portal to="sidepanel">
        <Portal to="modal">
            <ModalContainer
                v-if="showArchiveCourseIntegrationModal"
                :show-close-button="true"
                :remove-head-border-bottom="true"
                @close="closeArchiveModal">
                <template #head>
                    <div
                        class="edit-integration__archive-integration-head"
                    >
                        Archive Course Integration
                    </div>
                </template>
                <div
                    class="edit-integration__archive-modal"
                >
                    <span
                        class="edit-integration__archive-text"
                    >
                        Students and instructors will no longer be able to access
                        Pocket Prep directly from this Canvas course.
                    </span>
                    <div
                        class="edit-integration__archive-buttons"
                    >
                        <PocketButton
                            class="edit-integration__nevermind-btn"
                            type="secondary"
                            @click="closeArchiveModal"
                        >
                            Never mind
                        </PocketButton>
                        <PocketButton
                            class="edit-integration__archive-btn"
                            type="primary-red"
                            @click="archiveIntegration"
                        >
                            Archive
                        </PocketButton>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
        <SidePanel
            class="edit-integration"
            name="Edit Course Integration"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <div class="edit-integration__client-id-container">
                <span class="edit-integration__client-id-label">
                    Client ID:
                </span>
                {{ props.clientId }}
            </div>
            <PocketInput
                v-model="deploymentId"
                class="edit-integration__deployment-id"
                field-type="text"
                label="Deployment ID"
                :error="!!errors.length"
                :disabled="true"
            />
            <Errors
                v-if="errors.length"
                class="edit-integration__error-banner"
                :errors="errors"
            />
            <PocketInput
                v-model="courseName"
                class="edit-integration__course-name"
                field-type="text"
                label="Course Name"
                :error="!!errors.length"
            />
            <div class="edit-integration__course-details-container">
                <label class="edit-integration__course-details">
                    Course Details
                </label>
                <p class="edit-integration__course-details-subtext">
                    These details will automatically be associated with any students who click the SSO link on Canvas.
                </p>
            </div>
            <Banner
                class="edit-integration__banner"
            >
                <template #link>
                    <span />
                </template>
                If you change the Exam, Instructor, or Group, when students click the SSO link
                they will be assigned a new seat and added to the new Group and Instructor.
            </Banner>
            <div class="edit-integration__selects">
                <PocketSelect
                    v-model="exam"
                    class="edit-integration__select-exam"
                    label="Exam"
                    placeholder="Select Exam"
                    :showEmptyOption="true"
                    :data="mappedExams"
                />
                <PocketSelect
                    v-model="group"
                    :typeahead="!!exam"
                    class="edit-integration__select-group"
                    label="Group (optional)"
                    :data="mappedGroups"
                />
                <div
                    v-if="mappedInstructors"
                    class="edit-integration__instructors"
                >
                    <div class="edit-integration__instructors-label">
                        Instructors (optional)
                    </div>
                    <div
                        v-for="(option, index) in instructorOptions"
                        :key="option.value"
                        class="edit-integration__instructor-row"
                    >
                        <PocketSelect
                            v-model="instructorOptions[index]"
                            class="edit-integration__select-instructor"
                            :data="mappedInstructors"
                        />
                        <div
                            class="edit-integration__instructor-row-remove"
                            @click="instructorOptions[index]?.value && removeInstructor(instructorOptions[index].value)"
                            @mouseover="setRemoveInstructorTooltip(`${index}_${instructorOptions[index]?.value}`)"
                            @mouseout="clearRemoveInstructorTooltip"
                        >
                            <Tooltip
                                v-if="showRemoveInstructorTooltip === `${index}_${instructorOptions[index]?.value}`"
                                theme="leftalign"
                            >
                                Remove Instructor
                            </Tooltip>
                            <Icon type="close" />
                        </div>
                        <div
                            v-if="index === instructorOptions.length - 1"
                            class="edit-integration__instructor-row-add"
                            :class="{
                                'edit-integration__instructor-row-add--not-enabled': !instructorOptions[index]?.value
                            }"
                            @click="addInstructor(instructorOptions[index]?.value)"
                            @mouseover="setAddInstructorTooltip(`${index}_${instructorOptions[index]?.value}`)"
                            @mouseout="clearAddInstructorTooltip"
                        >
                            <Tooltip
                                v-if="instructorOptions[index]?.value
                                    && showAddInstructorTooltip === `${index}_${instructorOptions[index].value}`"
                                theme="leftalign"
                            >
                                Add Instructor
                            </Tooltip>
                            <Icon type="addCircle" />
                        </div>
                    </div>
                </div>
            </div>
            <template #details>
                <PocketButton
                    class="edit-integration__archive-btn"
                    type="tertiary-red"
                    @click="openArchiveModal"
                >
                    Archive Course Integration
                </PocketButton>
            </template>
            <template #action>
                <PocketButton
                    class="edit-integration__connect-btn"
                    :disabled="isLoading
                        || !deploymentId
                        || (!!errors.length && !!deploymentId)
                        || !exam"
                    @click="editIntegration"
                >
                    Save Changes
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.edit-integration {
    :deep(*) {
        .uikit-side-panel__content {
            padding: 36px 32px;
        }
        .uikit-tooltip {
            position: absolute;
            top: -30px;
            left: 22px;
        }

        .banner__text {
            margin: 0;
        }
    }

    &__course-name,
    &__deployment-id {
        margin-top: 24px;
    }

    &__error-banner {
        margin-top: 12px;
    }

    &__label {
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        margin: 0 0 4px 12px;
        color: $pickled-bluewood;
        display: block;
    }

    &__client-id-container {
        font-size: 13px;
        line-height: 20px;
    }

    &__client-id-label {
        font-weight: 600;
    }

    &__course-details-container {
        margin-top: 60px;
    }

    &__course-details {
        font-weight: 600;
    }

    &__course-details-subtext {
        line-height: 20px;
        margin: 7px 8px 0 0;
    }

    &__banner {
        margin-top: 24px;
        padding: 12px 24px 12px 16px;
        font-size: 15px;
    }

    &__select-exam,
    &__select-instructor,
    &__select-group {
        margin-top: 24px;
    }

    &__select-instructor {
        width: 433px;
        margin: 0;
    }

    &__instructors-label {
        margin: 27px 0 -19px 12px;
        font-size: 13px;
        font-weight: 600;
        color: $pickled-bluewood;
    }

    &__instructor-row {
        height: 36px;
        display: flex;
        align-items: center;
        margin-top: 23px;
    }

    &__instructor-row-add,
    &__instructor-row-remove {
        position: relative;
        padding: 4px 0 0 12px;
        color: $brand-blue;

        &--not-enabled {
            color: $fog;
        }
    }


    &__archive-integration-head {
        margin: 60px auto 25px;
        font-size: 18px;
        color: $brand-black;
        font-weight: 600;
    }

    &__archive-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 374px;
        box-sizing: border-box;
    }

    &__nevermind-btn {
        margin-right: 18px;
    }

    &__archive-buttons {
        display: flex;
        margin: 32px 0 24px;
    }

    &__archive-text {
        font-size: 15px;
        color: $ash;
        width: 294px;
        text-align: center;
        margin: 20px 0 0;
    }
}
</style>
